// =====================================================
// BUDGETS API CLIENT - Módulo de Presupuestos
// =====================================================
import { api } from './client';
import type {
  BudgetResponse,
  BudgetListItem,
  PaginatedBudgets,
  BudgetQueryParams,
  BudgetMedicalActsQueryParams,
  BudgetMedicalActInfo,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  AddBudgetLineRequest,
  LoadTemplateRequest,
  SendBudgetRequest,
  AcceptBudgetRequest,
  RejectBudgetRequest,
  CreateRectificationRequest,
  CalculatedPriceInfo,
} from '../types/budget';

import type { PatientFinancialSummary } from '../types/patient';
const BASE_URL = '/budgets';

export const budgetsApi = {
  // ==========================================================================
  // CRUD PRINCIPAL
  // ==========================================================================

  /**
   * Listar presupuestos con paginación y filtros
   */
  getAll: async (params?: BudgetQueryParams): Promise<PaginatedBudgets> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    const url = query ? `${BASE_URL}?${query}` : BASE_URL;
    return api.get<PaginatedBudgets>(url);
  },

  /**
   * Obtener presupuesto por ID
   */
  getById: async (id: string): Promise<BudgetResponse> => {
    return api.get<BudgetResponse>(`${BASE_URL}/${id}`);
  },

  /**
   * Crear nuevo presupuesto
   */
  create: async (data: CreateBudgetRequest): Promise<BudgetResponse> => {
    return api.post<BudgetResponse>(BASE_URL, data);
  },

  /**
   * Actualizar presupuesto (solo BORRADOR)
   */
  update: async (id: string, data: UpdateBudgetRequest): Promise<BudgetResponse> => {
    return api.patch<BudgetResponse>(`${BASE_URL}/${id}`, data);
  },

  /**
   * Eliminar presupuesto (solo BORRADOR)
   */
  delete: async (id: string): Promise<void> => {
    return api.delete(`${BASE_URL}/${id}`);
  },

  // ==========================================================================
  // GESTIÓN DE LÍNEAS
  // ==========================================================================

  /**
   * Añadir línea al presupuesto
   */
  addLine: async (budgetId: string, data: AddBudgetLineRequest): Promise<BudgetResponse> => {
    return api.post<BudgetResponse>(`${BASE_URL}/${budgetId}/lines`, data);
  },

  /**
   * Eliminar línea del presupuesto
   */
  removeLine: async (budgetId: string, lineId: string): Promise<BudgetResponse> => {
    return api.delete<BudgetResponse>(`${BASE_URL}/${budgetId}/lines/${lineId}`);
  },

  /**
   * Cargar plantilla en presupuesto
   */
  loadTemplate: async (budgetId: string, data: LoadTemplateRequest): Promise<BudgetResponse> => {
    return api.post<BudgetResponse>(`${BASE_URL}/${budgetId}/load-template`, data);
  },

  // ==========================================================================
  // FLUJO DE ESTADOS
  // ==========================================================================

  /**
   * Enviar presupuesto (BORRADOR → ENVIADO)
   */
  resend: async (id: string, email: string): Promise<void> =>
    api.post(`${BASE_URL}/${id}/resend`, { email }),

  send: async (id: string, data?: SendBudgetRequest): Promise<BudgetResponse> => {
    return api.post<BudgetResponse>(`${BASE_URL}/${id}/send`, data || {});
  },

  /**
   * Aceptar presupuesto (ENVIADO → ACEPTADO)
   */
  /**
   * Aceptar presupuesto — requiere PDF firmado obligatorio (multipart/form-data)
   */
  accept: async (id: string, data?: AcceptBudgetRequest, signedDocument?: File): Promise<BudgetResponse> => {
    const token = localStorage.getItem('imi_access_token') || '';
    const formData = new FormData();
    if (data?.notes) formData.append('notes', data.notes);
    if (signedDocument) formData.append('signedDocument', signedDocument);
    const response = await fetch('/api/v1' + BASE_URL + '/' + id + '/accept', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + token },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Error al aceptar el presupuesto' }));
      throw new Error(err.message || 'Error al aceptar el presupuesto');
    }
    return response.json();
  },

  /**
   * Rechazar presupuesto (ENVIADO → RECHAZADO)
   */
  reject: async (id: string, data: RejectBudgetRequest): Promise<BudgetResponse> => {
    return api.post<BudgetResponse>(`${BASE_URL}/${id}/reject`, data);
  },

  /**
   * Crear rectificación (nueva versión)
   */
  createRectification: async (id: string, data?: CreateRectificationRequest): Promise<BudgetResponse> => {
    return api.post<BudgetResponse>(`${BASE_URL}/${id}/rectify`, data || {});
  },

  // ==========================================================================
  // UTILIDADES
  // ==========================================================================

  /**
   * Buscar actos médicos con precios calculados
   */
  searchMedicalActs: async (params?: BudgetMedicalActsQueryParams): Promise<BudgetMedicalActInfo[]> => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
    }
    const query = searchParams.toString();
    const url = query ? `${BASE_URL}/search/medical-acts?${query}` : `${BASE_URL}/search/medical-acts`;
    return api.get<BudgetMedicalActInfo[]>(url);
  },

  /**
   * Calcular precio de un acto médico
   */
  calculatePrice: async (medicalActId: string, tariffId?: string): Promise<CalculatedPriceInfo> => {
    const searchParams = new URLSearchParams();
    if (tariffId) {
      searchParams.append('tariffId', tariffId);
    }
    const query = searchParams.toString();
    const url = query
      ? `${BASE_URL}/calculate-price/${medicalActId}?${query}`
      : `${BASE_URL}/calculate-price/${medicalActId}`;
    return api.get<CalculatedPriceInfo>(url);
  },

  /**
   * Obtener presupuestos de un paciente
   */
  getByPatient: async (patientId: string): Promise<BudgetListItem[]> => {
    return api.get<BudgetListItem[]>(`${BASE_URL}/patient/${patientId}`);
  },

  /**
   * Obtener historial de versiones
   */
  getVersionHistory: async (id: string): Promise<BudgetListItem[]> => {
    return api.get<BudgetListItem[]>(`${BASE_URL}/${id}/versions`);
  },

  /**
   * Resumen financiero agregado del paciente en el centro activo.
   * Incluye totales y evolución mensual (últimos 12 meses).
   */
  getPatientFinancialSummary: async (patientId: string): Promise<PatientFinancialSummary> => {
    return api.get<PatientFinancialSummary>(`${BASE_URL}/patient/${patientId}/financial-summary`);
  },

  // ==========================================================================
  // AUDITORÍA Y DOCUMENTOS
  // ==========================================================================

  /**
   * Obtener historial de auditoría de un presupuesto (y su familia de versiones)
   * Solo visible para GESTOR_SEDE y ADMIN
   */
  getAuditLog: async (id: string): Promise<any[]> => {
    return api.get<any[]>(`${BASE_URL}/${id}/audit`);
  },

  /**
   * Descargar PDF de un presupuesto como Blob
   * Usa fetch directo porque la respuesta es binaria (no JSON)
   */
  downloadPdf: async (id: string): Promise<Blob> => {
    const token = localStorage.getItem('imi_access_token') || '';
    const response = await fetch(`/api/v1${BASE_URL}/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const err = await response.text().catch(() => 'Error desconocido');
      throw new Error(`Error al descargar el PDF: ${err}`);
    }
    return response.blob();
  },
};

// ── API de pagos ─────────────────────────────────────────────────────────────
import type { BudgetPayment, CreatePaymentRequest, UpdatePaymentRequest } from '../types/budget';

const BASE_PAYMENTS = (budgetId: string) => `/budgets/${budgetId}/payments`;

export const paymentsApi = {
  getAll: async (budgetId: string): Promise<BudgetPayment[]> => {
    return api.get<BudgetPayment[]>(BASE_PAYMENTS(budgetId));
  },

  create: async (budgetId: string, data: CreatePaymentRequest): Promise<BudgetPayment> => {
    return api.post<BudgetPayment>(BASE_PAYMENTS(budgetId), data);
  },

  update: async (budgetId: string, paymentId: string, data: UpdatePaymentRequest): Promise<BudgetPayment> => {
    return api.patch<BudgetPayment>(`${BASE_PAYMENTS(budgetId)}/${paymentId}`, data);
  },

  cancel: async (budgetId: string, paymentId: string, reason?: string): Promise<BudgetPayment> => {
    return api.delete<BudgetPayment>(`${BASE_PAYMENTS(budgetId)}/${paymentId}`, {
      body: JSON.stringify({ reason }),
    });
  },
};
